import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { BadgeCheck, Sparkles, Trash2, Undo2 } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAdminCollection } from './hooks';
import { EmptyState } from './adminUi';
import Stars from '../../components/Stars';
import type { Testimonial } from '../../lib/types';
import { formatDateTimeMs } from '../../lib/format';

export default function TestimonialsAdmin() {
  const { items, loading } = useAdminCollection<Testimonial>('testimonials');
  const pending = items.filter((t) => !t.approved).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  const approved = items.filter((t) => t.approved).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));

  const remove = async (t: Testimonial) => {
    if (confirm(`Delete the review from ${t.name}?`)) await deleteDoc(doc(db(), 'testimonials', t.id));
  };

  const Card = ({ t }: { t: Testimonial }) => (
    <div className={`card p-5 ${!t.approved ? 'border-amber-400/30' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <Stars rating={t.rating} size={14} />
          <p className="mt-1 font-display font-semibold text-white">
            {t.name}
            {t.vehicle && <span className="ml-2 text-xs font-normal text-zinc-500">{t.vehicle}</span>}
            {t.featured && (
              <span className="ml-2 rounded-full bg-accent-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-accent-300">Featured</span>
            )}
          </p>
        </div>
        <span className="text-xs text-zinc-600">{t.createdAt ? formatDateTimeMs(t.createdAt) : ''}</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-zinc-300">“{t.text}”</p>
      <div className="mt-4 flex flex-wrap gap-2 border-t border-white/[0.06] pt-4">
        {!t.approved ? (
          <button className="btn-primary !px-4 !py-2 text-xs" onClick={() => updateDoc(doc(db(), 'testimonials', t.id), { approved: true })}>
            <BadgeCheck size={14} /> Approve & Publish
          </button>
        ) : (
          <>
            <button
              className="btn-ghost !px-4 !py-2 text-xs"
              onClick={() => updateDoc(doc(db(), 'testimonials', t.id), { featured: !t.featured })}
            >
              <Sparkles size={14} /> {t.featured ? 'Un-feature' : 'Feature on homepage'}
            </button>
            <button className="btn-ghost !px-4 !py-2 text-xs" onClick={() => updateDoc(doc(db(), 'testimonials', t.id), { approved: false, featured: false })}>
              <Undo2 size={14} /> Unpublish
            </button>
          </>
        )}
        <div className="flex-1" />
        <button className="flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300" onClick={() => remove(t)}>
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="font-display text-2xl font-bold text-white">Reviews</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Customer-submitted reviews wait here for approval before they appear on the site. Feature
        your best three on the homepage.
      </p>

      {loading ? (
        <EmptyState text="Loading…" />
      ) : (
        <>
          <h2 className="mt-8 flex items-center gap-2 font-display font-bold text-white">
            Awaiting Approval
            {pending.length > 0 && (
              <span className="rounded-full bg-amber-400/15 px-2.5 py-0.5 text-xs font-bold text-amber-300">{pending.length}</span>
            )}
          </h2>
          {pending.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-500">Nothing waiting — new reviews from the website land here.</p>
          ) : (
            <div className="mt-4 space-y-4">{pending.map((t) => <Card key={t.id} t={t} />)}</div>
          )}

          <h2 className="mt-10 font-display font-bold text-white">Published ({approved.length})</h2>
          {approved.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-500">No published reviews yet.</p>
          ) : (
            <div className="mt-4 space-y-4">{approved.map((t) => <Card key={t.id} t={t} />)}</div>
          )}
        </>
      )}
    </div>
  );
}
